import { CheckCircle2, LogOut, Mail, ShieldCheck, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Badge } from '../components/UI'
import { supabase } from '../lib/supabase'

type WorkspaceContext = {
  organisation_id: string
  organisation_name: string
  email_work_model: 'personal' | 'shared' | 'both'
  membership_role: string
  team_id: string | null
  team_name: string | null
  display_name: string | null
  account_email: string | null
}

type AccessibleMailbox = {
  id: string
  email_address: string
  provider: 'google' | 'microsoft'
  status: string
  mailbox_type: 'personal' | 'shared'
  is_primary_personal: boolean
}

export function EmployeeWorkspace() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [context, setContext] = useState<WorkspaceContext | null>(null)
  const [mailboxes, setMailboxes] = useState<AccessibleMailbox[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)

  async function load() {
    if (!supabase) return

    const [{ data: ctxData, error: ctxError }, { data: mailboxData, error: mailboxError }] = await Promise.all([
      supabase.rpc('get_employee_workspace_context'),
      supabase.rpc('get_extension_mailboxes')
    ])

    if (ctxError) {
      setMessage(ctxError.message)
      setLoading(false)
      return
    }

    if (mailboxError) setMessage(mailboxError.message)
    setContext((ctxData?.[0] ?? null) as WorkspaceContext | null)
    setMailboxes((mailboxData ?? []) as AccessibleMailbox[])
    setLoading(false)
  }

  useEffect(() => {
    const connected = params.get('connected')
    const error = params.get('error')
    const joined = params.get('joined')

    if (joined === '1') setMessage('You joined the organisation. Connect the work inbox you use with customers to finish setup.')
    if (connected) setMessage(`${connected === 'google' ? 'Google' : 'Microsoft'} mailbox connected successfully. You do not need to authorise it again unless access is revoked.`)
    if (error) setMessage('The mailbox connection could not be completed. Please try again.')

    load()
  }, [params])

  const personalMailboxes = useMemo(() => mailboxes.filter(mailbox => mailbox.mailbox_type === 'personal'), [mailboxes])
  const sharedMailboxes = useMemo(() => mailboxes.filter(mailbox => mailbox.mailbox_type === 'shared'), [mailboxes])
  const googlePersonalMailbox = personalMailboxes.find(mailbox => mailbox.provider === 'google')
  const microsoftPersonalMailbox = personalMailboxes.find(mailbox => mailbox.provider === 'microsoft')

  async function connect(provider: 'google' | 'microsoft') {
    if (!supabase) return
    setBusy(provider)
    setMessage('')

    const { data, error } = await supabase.functions.invoke(`${provider}-oauth-start`, {
      body: {
        mailboxType: 'personal',
        returnTo: '/employee'
      }
    })

    setBusy(null)

    if (error || !data?.url) {
      setMessage(error?.message || data?.error || 'Could not start mailbox authorization.')
      return
    }

    window.location.assign(data.url)
  }

  async function signOut() {
    if (!supabase) return
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  if (loading) return <div className="auth-loading"><span className="blue-spinner auth-spinner"/></div>
  if (!context) return <div className="auth-loading"><div className="auth-card"><h1>Employee workspace unavailable</h1><p>{message || 'No organisation membership was found.'}</p></div></div>

  const needsPersonalConnection = context.email_work_model === 'personal' || context.email_work_model === 'both'
  const hasPersonalConnection = personalMailboxes.length > 0

  return <div className="employee-space">
    <header className="employee-header">
      <div className="employee-brand">
        <img className="mail-percent-logo" src="/images/brand/mail-percent-logo.png" alt="Mail Percent"/>
        <div><strong>Mail Percent</strong><span>{context.organisation_name}</span></div>
      </div>
      <div className="employee-header-actions">
        <span>{context.display_name || context.account_email}</span>
        <button className="button ghost" onClick={() => navigate('/')}>Public site</button>
        <button className="button ghost" onClick={signOut}><LogOut size={15}/>Sign out</button>
      </div>
    </header>

    <main className="employee-main">
      <section className="employee-welcome">
        <span className="eyebrow">Employee workspace · {context.team_name || 'Team'}</span>
        <h1>{needsPersonalConnection && !hasPersonalConnection ? 'Connect your work inbox' : 'Your Mail Percent workspace is ready'}</h1>
        <p>Mail Percent connects to Gmail or Outlook through the provider API. You approve access once, then Mail Percent keeps your organisation's response, workload and SLA Performance reporting up to date without a browser extension.</p>
      </section>

      {message && <div className="notice"><CheckCircle2 size={20}/><div><strong>Workspace status</strong><span>{message}</span></div></div>}

      <div className="employee-setup-grid">
        {needsPersonalConnection && (
          <section className="employee-setup-card employee-provider-step">
            <div className="employee-step-icon"><Mail size={20}/></div>
            <span className="employee-step-number">Your work inbox</span>
            <h2>Connect the inbox you personally use with customers</h2>
            <p>Choose your email provider. The permissions screen appears during this connection, and you should not need to approve it again unless the provider connection is later revoked.</p>

            <div className="employee-provider-grid">
              <article className="employee-provider-card">
                <div className="employee-provider-card-head">
                  <img src="/images/integrations/gmail-logo.png" alt="Gmail"/>
                  <div><strong>Gmail</strong><span>Google Workspace or Gmail</span></div>
                </div>
                {googlePersonalMailbox ? (
                  <div className="employee-provider-connected"><div><strong>{googlePersonalMailbox.email_address}</strong><span>Personal work inbox</span></div><Badge tone="good">Connected</Badge></div>
                ) : (
                  <button className="button primary" onClick={() => connect('google')} disabled={busy === 'google'}>{busy === 'google' ? 'Opening Google…' : 'Connect Gmail'}</button>
                )}
              </article>

              <article className="employee-provider-card">
                <div className="employee-provider-card-head">
                  <img src="/images/integrations/outlook-logo.png" alt="Microsoft Outlook"/>
                  <div><strong>Outlook</strong><span>Microsoft 365 or Outlook</span></div>
                </div>
                {microsoftPersonalMailbox ? (
                  <div className="employee-provider-connected"><div><strong>{microsoftPersonalMailbox.email_address}</strong><span>Personal work inbox</span></div><Badge tone="good">Connected</Badge></div>
                ) : (
                  <button className="button secondary" onClick={() => connect('microsoft')} disabled={busy === 'microsoft'}>{busy === 'microsoft' ? 'Opening Microsoft…' : 'Connect Outlook'}</button>
                )}
              </article>
            </div>
          </section>
        )}

        {(context.email_work_model === 'shared' || context.email_work_model === 'both') && (
          <section className="employee-setup-card">
            <div className="employee-step-icon"><Users size={20}/></div>
            <span className="employee-step-number">Shared inboxes</span>
            <h2>Shared mailboxes assigned to you</h2>
            <p>A shared support or sales inbox is connected once by an authorised manager and then assigned to the employees who work from it.</p>
            {sharedMailboxes.length ? <div className="employee-mailbox-list">
              {sharedMailboxes.map(mailbox => <div className="employee-mailbox" key={mailbox.id}><div><strong>{mailbox.email_address}</strong><span>{mailbox.provider === 'google' ? 'Gmail' : 'Microsoft Outlook'} · Shared mailbox</span></div><Badge tone="good">Assigned</Badge></div>)}
            </div> : <div className="employee-waiting"><strong>No shared mailbox assigned yet</strong><span>Your manager can connect and assign shared customer inboxes from the manager workspace.</span></div>}
          </section>
        )}

        <section className="employee-setup-card">
          <div className="employee-step-icon"><ShieldCheck size={20}/></div>
          <span className="employee-step-number">After connection</span>
          <h2>No extension or repeated setup</h2>
          <p>Keep working normally in Gmail or Outlook. Mail Percent uses provider metadata and timestamps to calculate response cycles, workload, queues and SLA Performance. Reconnection is only needed if you or your provider revoke access.</p>
          <div className="employee-provider-connected">
            <div><strong>{needsPersonalConnection ? (hasPersonalConnection ? 'Mailbox connected' : 'Connection still required') : 'Organisation-managed shared inbox setup'}</strong><span>{needsPersonalConnection ? (hasPersonalConnection ? 'Your employee setup is complete.' : 'Connect Gmail or Outlook above to finish your setup.') : 'Your manager controls the shared mailbox connections used by your team.'}</span></div>
            <Badge tone={!needsPersonalConnection || hasPersonalConnection ? 'good' : 'neutral'}>{!needsPersonalConnection || hasPersonalConnection ? 'Ready' : 'Set up'}</Badge>
          </div>
        </section>
      </div>
    </main>
  </div>
}
